import { DiffEditor } from '@monaco-editor/react';
import { Button } from '../ui/button';
import { ArrowLeft, Columns, AlignJustify } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';

interface DiffViewerProps {
  oldContent: string;
  newContent: string;
  oldLabel: string;
  newLabel: string;
  templatePath: string;
  language?: string;
  onBack: () => void;
}

export default function DiffViewer({ oldContent, newContent, oldLabel, newLabel, templatePath, language = 'html', onBack }: DiffViewerProps) {
  const [inline, setInline] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [editorHeight, setEditorHeight] = useState(400);

  useEffect(() => {
    const updateHeight = () => {
      if (containerRef.current) {
        setEditorHeight(containerRef.current.clientHeight);
      }
    };
    updateHeight();
    const observer = new ResizeObserver(updateHeight);
    if (containerRef.current) observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="flex h-full flex-col bg-studio-surface">
      {/* Toolbar */}
      <div className="flex items-center justify-between border-b border-border px-4 py-2" data-tour="diff-viewer">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={onBack} className="h-7 text-xs gap-1.5">
            <ArrowLeft className="h-3.5 w-3.5" />
            Back
          </Button>
          <div className="h-4 w-px bg-border" />
          <span className="text-xs font-mono text-muted-foreground">
            Diff: {oldLabel} → {newLabel}
          </span>
          <span className="text-xs text-muted-foreground">({templatePath})</span>
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant={!inline ? 'secondary' : 'ghost'}
            size="sm"
            className="h-7 text-[11px] gap-1"
            onClick={() => setInline(false)}
          >
            <Columns className="h-3 w-3" />
            Split
          </Button>
          <Button
            variant={inline ? 'secondary' : 'ghost'}
            size="sm"
            className="h-7 text-[11px] gap-1"
            onClick={() => setInline(true)}
          >
            <AlignJustify className="h-3 w-3" />
            Unified
          </Button>
        </div>
      </div>

      {/* Labels */}
      <div className="flex border-b border-border text-[11px] text-muted-foreground">
        <div className="flex-1 px-4 py-1.5 border-r border-border bg-muted/30">{oldLabel}</div>
        <div className="flex-1 px-4 py-1.5 bg-muted/30">{newLabel}</div>
      </div>

      {/* Diff Editor */}
      <div className="flex-1 min-h-0" ref={containerRef}>
        <DiffEditor
          height={editorHeight}
          language={language}
          original={oldContent}
          modified={newContent}
          theme="vs"
          onMount={(editor) => {
            editor.updateOptions({ renderSideBySide: !inline });
          }}
          options={{
            fontSize: 13,
            fontFamily: "'JetBrains Mono', monospace",
            minimap: { enabled: false },
            scrollBeyondLastLine: false,
            readOnly: true,
            renderSideBySide: !inline,
            padding: { top: 8 },
            automaticLayout: true,
          }}
        />
      </div>
    </div>
  );
}
